import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { FlowContentBody, QuestionWrapper } from '../../styles/Containers';
import { BodySM, BodyXS, Heading3, HeroTitle } from '../../styles/Typography';
import { LayoutActions } from './LayoutActions';

export type CoverageResultsLayoutConfig = {
  title: string;
  subtitle: string;
  nextSteps: {
    heading: string;
    body: string[];
    /** Lead-in for the email sentence, e.g. "It'll go to"; the email and a full stop follow it. */
    emailLead: string;
  };
  assistance: {
    heading: string;
    title: string;
    body: string;
    linkTitle: string;
  };
  primaryButtonTitle: string;
};

type Props = CoverageResultsLayoutConfig & {
  /** "What happens next", the insurer request. */
  showNextSteps: boolean;
  /** Where the insurer request will be sent; the sentence is omitted when unknown. */
  email?: string;
  onPressPrimaryButton: () => void;
  /** Omit to hide the financial-assistance card. */
  onPressAssistance?: () => void;
};

export const CoverageResultsLayout: FunctionComponent<Props> = (props) => (
  <QuestionWrapper>
    <FlowContentBody>
      <Intro>
        <HeroTitle data-test="surveyQuestion">{props.title}</HeroTitle>
        <BodySM>{props.subtitle}</BodySM>
      </Intro>

      {props.showNextSteps && (
        <>
          <SectionHeading>{props.nextSteps.heading}</SectionHeading>
          {props.nextSteps.body.map((paragraph, index) => (
            <Paragraph key={index}>{paragraph}</Paragraph>
          ))}
          {props.email !== undefined && (
            <Paragraph data-testid="coverage-results-email">
              {props.nextSteps.emailLead} <Strong>{props.email}</Strong>.
            </Paragraph>
          )}
        </>
      )}

      {props.onPressAssistance !== undefined && (
        <>
          <SectionHeading>{props.assistance.heading}</SectionHeading>
          <AssistanceCard>
            <div>
              <CardTitle>{props.assistance.title}</CardTitle>
              <CardBody>{props.assistance.body}</CardBody>
            </div>
            <CardLink
              type="button"
              onClick={props.onPressAssistance}
              data-testid="coverage-results-assistance-link"
            >
              {props.assistance.linkTitle} →
            </CardLink>
          </AssistanceCard>
        </>
      )}
    </FlowContentBody>
    <LayoutActions
      primaryButtonTitle={props.primaryButtonTitle}
      onPressPrimaryButton={props.onPressPrimaryButton}
    />
  </QuestionWrapper>
);

// Left-aligned like the sections under it.
const Intro = styled.div`
  text-align: left;
  margin-bottom: ${({ theme }) => theme.spacing._24px};
`;

const SectionHeading = styled(Heading3)`
  text-align: left;
  margin-top: ${({ theme }) => theme.spacing._24px};
  margin-bottom: ${({ theme }) => theme.spacing._8px};
`;

const Paragraph = styled(BodySM)`
  text-align: left;
  color: ${Color.content.muted};
  margin: 0 0 ${({ theme }) => theme.spacing._12px};
`;

const Strong = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${Color.content.default};
`;

const AssistanceCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing._16px};
  text-align: left;
  padding: ${({ theme }) => theme.spacing._20px};
  border: 1px solid ${Color.decorative.default};
  border-radius: 16px;
`;

const CardTitle = styled(BodySM)`
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  margin: 0 0 ${({ theme }) => theme.spacing._8px};
`;

const CardBody = styled(BodyXS)`
  color: ${Color.content.muted};
  margin: 0;
`;

const CardLink = styled.button`
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  flex-shrink: 0;
  cursor: pointer;
  color: ${Color.content.default};
  text-decoration: underline;
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;
